import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowLeft, Wallet, Clock, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { apiClient } from '../lib/api.js';
import { Card, CardContent } from '../components/ui/Card.js';
import { Badge } from '../components/ui/Badge.js';
import { cn, formatBRL, formatDateTime } from '../lib/utils.js';

const STATUS_COLOR: Record<string, string> = {
  pending: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  approved: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  paid: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30',
  cancelled: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
};

const STATUS_LABEL: Record<string, string> = {
  pending: 'Aguardando liberação',
  approved: 'Aprovada',
  paid: 'Paga',
  cancelled: 'Cancelada (clawback)',
};

const KIND_LABEL: Record<string, string> = {
  main: 'Front',
  upsell: 'Pro',
  order_bump: 'Instalação',
  cross_sell: 'Extra',
};

export function MinhasComissoes(): JSX.Element {
  const { data, isLoading } = useQuery({
    queryKey: ['me-commissions'],
    queryFn: () => apiClient.meCommissions(),
    refetchInterval: 60_000,
  });

  if (isLoading || !data) {
    return <div className="p-8 text-muted-foreground">Carregando…</div>;
  }

  const { totals, byPlan, items } = data;

  return (
    <div className="p-6 space-y-6 max-w-[1200px] mx-auto">
      {/* Breadcrumb */}
      <Link
        to="/meu-trabalho"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Voltar pra Meu trabalho
      </Link>

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Wallet className="h-6 w-6 text-emerald-400" />
          Minhas comissões
        </h1>
        <p className="text-xs text-muted-foreground mt-1">
          Os valores aqui são exclusivos seus · histórico completo
        </p>
      </div>

      {/* Totais */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="border-amber-500/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                A receber
              </span>
              <Clock className="h-4 w-4 text-amber-400" />
            </div>
            <div className="text-3xl font-bold text-amber-300 tabular-nums">
              {formatBRL(totals.pendingCents)}
            </div>
            <div className="text-[10px] text-muted-foreground mt-1">
              libera após D+30 do gateway
            </div>
          </CardContent>
        </Card>

        <Card className="border-cyan-500/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                Já recebido
              </span>
              <CheckCircle2 className="h-4 w-4 text-cyan-400" />
            </div>
            <div className="text-3xl font-bold text-cyan-300 tabular-nums">
              {formatBRL(totals.paidCents)}
            </div>
            <div className="text-[10px] text-muted-foreground mt-1">
              já transferido pra você
            </div>
          </CardContent>
        </Card>

        <Card className="border-rose-500/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                Canceladas
              </span>
              <XCircle className="h-4 w-4 text-rose-400" />
            </div>
            <div className="text-3xl font-bold text-rose-300 tabular-nums">
              {formatBRL(totals.cancelledCents)}
            </div>
            <div className="text-[10px] text-muted-foreground mt-1">
              clawback por reembolso ou troca de plano
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quebra por plano */}
      {byPlan.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <h2 className="text-sm font-bold mb-3 flex items-center gap-2">
              Por plano de comissão
            </h2>
            <div className="space-y-2">
              {byPlan.map((p, idx) => (
                <div
                  key={`${p.plan}-${p.status}-${idx}`}
                  className="flex items-center justify-between text-sm border-b border-border/40 pb-2 last:border-0"
                >
                  <div className="flex items-center gap-2">
                    <code className="text-xs">{p.plan}</code>
                    <Badge variant="outline" className={cn('text-[10px]', STATUS_COLOR[p.status])}>
                      {STATUS_LABEL[p.status] ?? p.status}
                    </Badge>
                  </div>
                  <div className="text-right">
                    <div className="font-bold tabular-nums">{formatBRL(p.totalCents)}</div>
                    <div className="text-[10px] text-muted-foreground">{p.count} comissões</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Histórico detalhado */}
      <Card>
        <CardContent className="p-0">
          <div className="px-4 py-3 border-b border-border">
            <h2 className="text-sm font-bold">Histórico</h2>
          </div>
          {items.length === 0 ? (
            <div className="p-10 text-center space-y-2">
              <Wallet className="h-8 w-8 mx-auto text-muted-foreground" />
              <div className="text-sm font-medium">Ainda sem comissões registradas</div>
              <p className="text-xs text-muted-foreground max-w-md mx-auto">
                Quando uma venda for atribuída a você (pelo seu link ou por marcar lead como
                "recuperado"), a comissão correspondente aparece aqui.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-wider text-muted-foreground bg-muted/30 border-b border-border">
                  <tr>
                    <th className="px-4 py-2 text-left">Data</th>
                    <th className="px-4 py-2 text-left">Cliente</th>
                    <th className="px-4 py-2 text-left">Produto</th>
                    <th className="px-4 py-2 text-right">Valor venda</th>
                    <th className="px-4 py-2 text-right">% / fixo</th>
                    <th className="px-4 py-2 text-right">Você ganha</th>
                    <th className="px-4 py-2 text-left">Status</th>
                    <th className="px-4 py-2 text-left">Libera</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/40">
                  {items.map((c) => (
                    <tr key={c.id} className="hover:bg-muted/20">
                      <td className="px-4 py-2 text-xs text-muted-foreground tabular-nums">
                        {formatDateTime(c.createdAt)}
                      </td>
                      <td className="px-4 py-2">{c.contactName ?? '—'}</td>
                      <td className="px-4 py-2 text-xs">
                        {KIND_LABEL[c.order.productKind] ?? c.order.productKind}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">{formatBRL(c.order.grossCents)}</td>
                      <td className="px-4 py-2 text-right text-xs text-muted-foreground tabular-nums">
                        {Number(c.percent) > 0 ? `${(Number(c.percent) * 100).toFixed(0)}%` : 'R$ 30'}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums font-bold">
                        {formatBRL(c.amountCents)}
                      </td>
                      <td className="px-4 py-2">
                        <span className={cn('inline-block px-2 py-0.5 rounded text-[10px] border', STATUS_COLOR[c.status])}>
                          {STATUS_LABEL[c.status] ?? c.status}
                        </span>
                        {c.clawbackReason && (
                          <div className="text-[9px] text-rose-300 mt-0.5">
                            {c.clawbackReason}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2 text-xs text-muted-foreground tabular-nums">
                        {c.liberationDueDate
                          ? new Date(c.liberationDueDate).toLocaleDateString('pt-BR')
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-cyan-500/20 bg-cyan-500/5">
        <CardContent className="p-4">
          <div className="flex items-start gap-3 text-xs">
            <AlertCircle className="h-5 w-5 text-cyan-400 flex-shrink-0 mt-0.5" />
            <div className="space-y-1">
              <div className="font-bold text-sm text-cyan-300">Como funciona o pagamento</div>
              <p className="text-muted-foreground">
                As comissões ficam <strong>"a receber"</strong> por D+30 (janela de reembolso do
                gateway). Após esse prazo, são liberadas e transferidas pra você. Se o cliente pedir
                reembolso ou chargeback dentro dos 30 dias, a comissão vira <strong>cancelada (clawback)</strong>.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
